import moment from 'moment';
import { imageString, getStatusFromId } from '../../../helper';
import { BookingStatus } from '../../../helper/BookingStatus';

export function getAmenityCoverImageUrl(amenity) {
  const images = amenity?.images;
  if (!Array.isArray(images) || images.length === 0) return null;

  const sorted = [...images].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const url = sorted[0]?.url;
  if (!url || typeof url !== 'string' || !url.trim()) return null;

  const trimmed = url.trim();
  return trimmed.startsWith('http') ? trimmed : imageString(trimmed);
}

export function getBookingDetailHeaderTitle(booking) {
  if (isBookingBlock(booking)) {
    const name = booking?.amenity_name || booking?.title;
    return name ? `${name} Block` : 'Block';
  }
  const name = booking?.amenity_name || booking?.title;
  return name ? `${name} Booking` : 'Booking';
}

export function getBookingDetailStatusLabel(booking) {
  if (booking?.status === undefined || booking?.status === null) return '';
  return getStatusFromId(booking.status) || '';
}

export function formatBookingDetailDate(instant) {
  if (!instant) return '—';
  const m = moment.parseZone(instant);
  if (!m.isValid()) return '—';
  return m.format('MMMM D, YYYY');
}

export function formatBookingDetailTime(instant) {
  if (!instant) return '—';
  const m = moment.parseZone(instant);
  if (!m.isValid()) return '—';
  return m.format('h:mm a');
}

/** Compact list/CSV time, e.g. `11:30am` */
export function formatBookingListTimeCompact(instant) {
  if (!instant) return '—';
  const m = moment.parseZone(instant);
  if (!m.isValid()) return '—';
  return m.format('h:mma').toLowerCase();
}

export function getBookingAmenityLabel(booking) {
  const name = booking?.amenity_name || booking?.title;
  return name || '—';
}

export function isMidnightCheckoutEnd(start, end) {
  if (!start?.isValid?.() || !end?.isValid?.()) return false;
  const nextDayMidnight = start.clone().add(1, 'day').startOf('day');
  return end.isSame(nextDayMidnight, 'minute') || end.isSame(nextDayMidnight, 'second');
}

function parseBookingStartEnd(booking) {
  const start = booking?.start_date ? moment.parseZone(booking.start_date) : null;
  const end = booking?.end_date ? moment.parseZone(booking.end_date) : null;
  const startValid = start?.isValid() ?? false;
  const endValid = end?.isValid() ?? false;
  return { start: startValid ? start : null, end: endValid ? end : null };
}

export function isMultiDayBookingForList(booking) {
  const { start, end } = parseBookingStartEnd(booking);
  if (!start || !end) return false;
  if (start.isSame(end, 'day')) return false;
  if (isMidnightCheckoutEnd(start, end)) return false;
  return true;
}

export function formatBookingListEndTime(endInstant, startInstant) {
  if (!endInstant) return '—';
  const end = moment.parseZone(endInstant);
  if (!end.isValid()) return '—';

  const start = startInstant ? moment.parseZone(startInstant) : null;
  if (start?.isValid()) {
    if (isMidnightCheckoutEnd(start, end)) return '12:00 am';
    if (start.isSame(end, 'day') && end.format('HH:mm') === '23:59') return '12:00 am';
  } else if (end.format('HH:mm') === '23:59') {
    return '12:00 am';
  }

  return formatBookingListTimeCompact(endInstant);
}

export function formatBookingListTimeRange(booking) {
  const { start, end } = parseBookingStartEnd(booking);
  if (!start) return '—';
  const startLabel = formatBookingListTimeCompact(booking.start_date);
  if (!end) return startLabel;
  const endLabel = formatBookingListEndTime(booking.end_date, booking.start_date);
  return `${startLabel}\u2013${endLabel}`;
}

function formatListDateStack(instant) {
  const m = moment.parseZone(instant);
  if (!m.isValid()) return { month: '—', day: '—' };
  return { month: m.format('MMM'), day: m.format('D') };
}

export function getBookingListDateDisplay(booking) {
  const { start, end } = parseBookingStartEnd(booking);
  if (!start) return { type: 'single', label: '—' };

  if (!isMultiDayBookingForList(booking)) {
    return { type: 'single', label: start.format('MMM D') };
  }

  return {
    type: 'range',
    start: formatListDateStack(booking.start_date),
    end: formatListDateStack(booking.end_date)
  };
}

export function getBookingListTimeDisplay(booking) {
  if (!isMultiDayBookingForList(booking)) {
    return { type: 'range', text: formatBookingListTimeRange(booking) };
  }

  return {
    type: 'inOut',
    checkIn: formatBookingListTimeCompact(booking.start_date),
    checkOut: formatBookingListTimeCompact(booking.end_date)
  };
}

/** Flat strings for CSV export */
export function formatBookingListDateForCsv(booking) {
  const display = getBookingListDateDisplay(booking);
  if (display.type === 'single') return display.label;
  return `${display.start.month} ${display.start.day} – ${display.end.month} ${display.end.day}`;
}

export function formatBookingListTimeForCsv(booking) {
  const display = getBookingListTimeDisplay(booking);
  if (display.type === 'range') return display.text;
  return `IN ${display.checkIn} / OUT ${display.checkOut}`;
}

const isBookingInFuture = (booking) => {
  if (!booking?.start_date) return true;
  return moment(booking.start_date).isAfter(moment());
};

export function canShowCancelBooking(booking) {
  if (!booking?.id) return false;

  const status = parseInt(booking.status, 10);
  if (status === BookingStatus.Cancelled || status === BookingStatus.Archived) {
    return false;
  }

  return isBookingInFuture(booking);
}

export function isBookingStatusPending(status) {
  return parseInt(status, 10) === BookingStatus.Pending;
}

export function bookingRequiresApprovalOrPayment(booking) {
  return Boolean(booking?.require_manager_approval) || Boolean(booking?.require_payment);
}

export function canShowEditBooking(booking) {
  if (!booking?.id) return false;

  const status = parseInt(booking.status, 10);
  if (status === BookingStatus.Cancelled || status === BookingStatus.Archived) {
    return false;
  }

  if (!isBookingInFuture(booking)) return false;

  if (isBookingBlock(booking)) return true;

  if (booking?.require_payment) {
    return false;
  }

  if (booking?.require_manager_approval) {
    return status === BookingStatus.Pending;
  }

  return true;
}

export function isBookingBlock(booking) {
  return parseInt(booking?.type, 10) === 1;
}

export function canShowApproveBooking(booking) {
  if (!booking?.id) return false;
  if (isBookingBlock(booking)) return false;
  return isBookingStatusPending(booking.status);
}

export function isApproveBookingDisabled(booking) {
  return !isBookingInFuture(booking);
}
